import React from 'react';
import * as yup from 'yup';
import last from 'lodash/last';
import { css } from '@emotion/core';
import { useSelector } from 'react-redux';
import Button from 'react-bootstrap/Button';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  get,
  useForm,
  FormProvider,
  useFieldArray,
  useFormContext,
  FieldArrayWithId,
} from 'react-hook-form';

import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import NvIcon from 'shared/components/nv-icon';
import NvFroala from 'froala/components/nv-froala';
import { AngularServicesContext } from 'react-app';
import { PopoversContainerContext } from 'shared/react-utils';
import { gray3, primary, success } from 'styles/global_defaults/colors';
import ClickableContainer from 'components/clickable-container';
import CourseRow from 'learning_journeys/components/course-row';
import { FroalaViewMode } from 'froala/helpers/nv-froala-constants';
import LoadingPlaceholder from 'shared/components/loading-placeholder';
import { boldFontWeight, openSans } from 'styles/global_defaults/fonts';
import LearningStatus from 'learning_journeys/components/learning-status';
import { updateJourneyCollections } from 'redux/actions/learning-journeys';
import useAngularStateManager from 'shared/hooks/use-angular-state-manager';
import { Ref as BrandingHeaderRef } from 'shared/components/branding-header';
import { CollectionCompletionStats, Course, CourseRegistrationType } from 'redux/schemas/models/course';
import OfferedToSection from 'learning_journeys/components/offered-to-section';
import InputCourseSearch from 'learning_journeys/components/input-course-search';
import useJourneyFormUtils from 'learning_journeys/hooks/use-journey-form-utils';
import OfferingsBrandingHeader from 'offerings/components/offering-branding-header';
import {
  openSavingOverlay,
  closeSavingOverlay,
} from 'redux/actions/saving-overlay';
import {
  getJourney,
  getJourneyCollections,
} from 'redux/selectors/learning-journeys';
import { getCoursesArray } from 'redux/selectors/course';
import { getCurrentUserEnrollments } from 'redux/selectors/users';
import { CombinedCourse } from 'redux/schemas';
import {
  halfSpacing,
  doubleSpacing,
  tripleSpacing,
  standardSpacing,
  threeQuartersSpacing,
} from 'styles/global_defaults/scaffolding';
import {
  LearningJourneyContext,
} from 'learning_journeys/components/learning-journey';
import {
  FormCollectionHeader,
} from 'learning_journeys/components/collection-header';
import useCourseUsageRegistry, {
  CourseUsageRegistryProvider,
  useCourseUsageRegistryContext,
} from 'learning_journeys/hooks/use-course-usage-regisitry';
import {
  CompletionCriteria,
  CompletionCriteriaLabel,
  FormCompletionCriteriaDropdown,
  Value as CompletionCriteriaValue,
} from 'learning_journeys/components/completion-criteria-dropdown';
import NvNoResults from 'shared/components/nv-no-results-panel';
import ResponsivelyEmbeddedAngularHTML from 'shared/components/responsively-embedded-angular-html';

import CoursesCarousel from './courses-carousel';
import CollectionSeparator from './divider';
import { config } from '../../../config/pendo.config.json';

const MAX_COLLECTION_NAME_LENGTH = 190;

const getEmptyCollection = () => ({
  name: '',
  courses: [],
  description: '',
  completionCriteria: {
    meta: null,
    value: CompletionCriteria.ALL_IN_ORDER,
  },
});

const limitedWidthContainerStyles = css`
  width: 100%;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  padding-left: ${standardSpacing}px;
  padding-right: ${standardSpacing}px;
`;

const masterContainerStyles = css`
  padding-bottom: 140px;
  padding-top: ${doubleSpacing}px;
`;

const journeyDetailsContext = React.createContext<{
  catalogId: string,
  isViewMode: boolean,
}>(null);

/**
 * Type to prevent ts(2589) error "Type instantiation is excessively deep and
 * possibly infinite."
 */
type InterceptedCourse = Omit<Course, 'collections'>;

type FormCollection = {
  id: number,
  name: string,
  description: string,
  courses: InterceptedCourse[],
  completionCriteria: CompletionCriteriaValue,
};

type FormData = {
  description: string,
  collections: FormCollection[],
};

const JourneyContent = () => {
  const dispatch = useAppDispatch();
  const courses = useSelector(getCoursesArray);
  const angularServices = React.useContext(AngularServicesContext);
  const { $scope, $state } = angularServices;
  const { catalogId, isViewMode } = React.useContext(journeyDetailsContext);
  const currentJourney = useSelector((state) => getJourney(state, catalogId));

  const {
    previousStatesEntered,
    previousParamsEntered,
  } = useAngularStateManager();

  const lastStateEntered: any = last(previousStatesEntered);
  const lastParamsEntered = last(previousParamsEntered);

  const journeyCollections = useSelector(
    (state) => getJourneyCollections(state, catalogId),
  );

  const userEnrollment = useSelector((state) => Object.values(state.models.enrollments)
    .find((enrollment: any) => currentJourney.id === enrollment.courseId));

  const isJourneyEmpty = currentJourney.coursesCount === 0;
  const isJourneyComplete = !!userEnrollment?.completionStatus
    || (currentJourney?.completionStats?.required > 0 && currentJourney.completionStats?.collections.every(({ completed, required }) => completed >= required));

  const validationSchemaRef = React.useRef(yup.object().shape({
    collections: yup.array().of(yup.object().shape({
      name: yup.string().max(MAX_COLLECTION_NAME_LENGTH, t.VALIDATION.MAX_LENGTH(`${MAX_COLLECTION_NAME_LENGTH}`)),
      completionCriteria: yup.object().shape({
        value: yup.number().oneOf([
          CompletionCriteria.ALL_IN_ORDER,
          CompletionCriteria.ALL_IN_ANY_ORDER,
          CompletionCriteria.AT_LEAST_X_COURSES,
        ]),
        meta: yup.lazy((value) => {
          if (value === '') {
            return yup.string().min(1, t.VALIDATION.REQUIRED());
          }

          if (typeof value === 'number') {
            return yup.number().min(1, t.VALIDATION.REQUIRED());
          }

          return yup.mixed();
        }),
      }),
    })),
  }));

  const getInitialFormData = () => {
    if (currentJourney.collectionIds?.length) {
      return {
        description: currentJourney.description,
        collections: journeyCollections.map((collection) => ({
          id: collection.id,
          key: collection.id,
          name: collection.name,
          description: collection.description,
          courses: collection.courseIds.map((courseId) => courses.find((course) => course.id === courseId)),
          completionCriteria: {
            meta: collection.numRequiredCourses,
            value: collection.completionSettings,
          },
        })),
      };
    }

    return {
      collections: [getEmptyCollection()],
      description: currentJourney.description,
    };
  };

  const initialFormData = getInitialFormData();

  const courseUsageRegistry = useCourseUsageRegistry(
    initialFormData.collections,
  );

  const methods = useForm<FormData>({
    mode: 'onChange',
    defaultValues: initialFormData,
    resolver: yupResolver(validationSchemaRef.current),
  });

  const { isDirty, isValid } = methods.formState;

  const journeyFormUtils = useJourneyFormUtils(methods.formState);

  const {
    fields: collections,
    swap: swapCollection,
    append: appendToCollections,
    remove: removeFromCollections,
  } = useFieldArray<FormData, 'collections', 'key'>({
    name: 'collections',
    control: methods.control,
  });

  React.useEffect(() => {
    if (isJourneyComplete && isViewMode) {
      $scope.ConfettiAnimation.showConfetti();
    }
  }, [$scope.ConfettiAnimation, isJourneyComplete, isViewMode]);

  const styles = css`
    ${masterContainerStyles};

    .offered-to {
      margin-bottom: ${doubleSpacing}px;
    }

    .separator {
      margin: 45px auto;
    }

    textarea {
      min-height: 100px;
      max-height: 200px;
    }

    .collections {
      .collection {
        .limited-width-collection-container {
          margin-bottom: ${isViewMode ? standardSpacing : 0}px;
        }

        .collection-header {
          margin-bottom: ${standardSpacing}px;
        }

        .collection-completion-criteria {
          margin-bottom: ${standardSpacing}px;
        }

        .collection-description {
          margin-bottom: ${standardSpacing}px;
        }

        .collection-courses {
          margin-bottom: ${standardSpacing}px;
        }

        .courses-search {
          justify-content: center;

          .autocomplete {
            flex: 1;
            max-width: 400px;
          }
        }
      }
    }

    .journey-footer {
      display: flex;
      flex-direction: column;
      align-items: center;

      .icon-container {
        display: flex;
        align-items: center;
        margin: ${doubleSpacing}px 0 ${standardSpacing}px;

        .separator {
          margin: 0;
        }

        i {
          margin: 0 ${threeQuartersSpacing}px;
          color: ${success};
        }
      }

      .text {
        color: ${gray3};
        text-align: center;
        font-weight: ${boldFontWeight};

        a {
          color: ${primary};
        }
      }
    }

    .new-collection {
      margin-bottom: ${tripleSpacing}px;
    }
  `;

  const handleNewCollectionButtonClick = () => appendToCollections(getEmptyCollection());

  const handleFormCancel = () => $state.go(lastStateEntered.name, lastParamsEntered);

  const handleFormSubmit = (data: FormData) => {
    const {
      description,
      collections: newCollections,
    } = data;

    dispatch(openSavingOverlay());

    methods.reset(data);

    wrapThunkAction(dispatch(updateJourneyCollections({
      catalogId,
      description,
      collections: newCollections.map((collection, index) => ({
        index,
        id: collection.id,
        name: collection.name,
        description: collection.description,
        completionSettings: collection.completionCriteria.value,
        courseIds: collection.courses.map((course) => course.id),
        numRequiredCourses: collection.completionCriteria.meta || null,
      })),
    }))).then(
      () => {
        if ($state.params.isNewJourney) {
          $state.go('learning-journey-home', { catalogId });
        } else {
          $state.go(lastStateEntered.name, lastParamsEntered);
        }

        journeyFormUtils.showSuccessAlert(true);
      },
      journeyFormUtils.showErrorAlert,
    ).finally(() => dispatch(closeSavingOverlay()));
  };

  // Logic to inform the badges/certificates of the learning journey
  let selectCase: 'both' | 'credly' | 'soa' = 'both';
  let credlyLink = '';
  const credlyBadgeEnabled = currentJourney?.credlyBadgeInfo?.id;

  if (credlyBadgeEnabled) {
    selectCase = 'credly';
    credlyLink = currentJourney?.credlyBadgeInfo?.url;
  }

  if (currentJourney?.soaCertificateEnabled) {
    selectCase = credlyBadgeEnabled ? 'both' : 'soa';
  }

  return (
    <CourseUsageRegistryProvider {...courseUsageRegistry}>
      {isViewMode && <LearningStatus isStatic />}
      <div css={styles}>
        {(isJourneyEmpty && isViewMode) ? (
          <div css={limitedWidthContainerStyles}>
            <NvNoResults
              action={() => {}}
              noResultsText={t.LEARNING_JOURNEYS.DETAILS.COURSES.EMPTY_RESULTS()}
            />
          </div>
        ) : (
          <FormProvider {...methods}>
            <div css={limitedWidthContainerStyles}>
              {currentJourney.typeOfRegistration === CourseRegistrationType.OPEN_BASED_ON_ENTITLEMENTS && (
                <OfferedToSection journey={currentJourney} className='offered-to' />
              )}
              {isViewMode ? (
                !!currentJourney.description && (
                  <ResponsivelyEmbeddedAngularHTML
                    className='journey-description'
                    angularServices={angularServices}
                    template={currentJourney.description}
                  />
                )
              ) : (
                <NvFroala
                  withForm
                  name='description'
                  immediateReactModelUpdate
                  preset={FroalaViewMode.NORMAL}
                  className='journey-description'
                  placeholder={t.LEARNING_JOURNEYS.DETAILS.JOURNEY_DESCRIPTION_PLACEHOLDER()}
                />
              )}
            </div>
            {(isViewMode ? !!currentJourney.description : true) && (
              <CollectionSeparator className='separator' />
            )}
            <div className='collections'>
              <CollectionCompletionsStatusProvider>
                {collections.map((collection, index, array) => (
                  <Collection
                    index={index}
                    collections={array}
                    key={collection.id}
                    collection={collection}
                    swapCollection={swapCollection}
                    removeFromCollections={removeFromCollections}
                    collectionCompletionStats={currentJourney.completionStats?.collections.find(({ id }) => id === collection.id)}
                  />
                ))}
              </CollectionCompletionsStatusProvider>
            </div>
            {!isViewMode && !!collections.length && (
              <CollectionSeparator className='separator' />
            )}
            {isViewMode ? (
              !isJourneyComplete && !isJourneyEmpty && (
                <div
                  className='journey-footer'
                  css={limitedWidthContainerStyles}
                >
                  <div className='icon-container'>
                    <CollectionSeparator className='separator' />
                    {currentJourney.soaCertificateEnabled || credlyBadgeEnabled
                      ? <NvIcon icon='badge' size='large' />
                      : <NvIcon icon='success' size='large' />}
                    <CollectionSeparator className='separator' />
                  </div>
                  <span className='text'>
                    {currentJourney.soaCertificateEnabled || credlyBadgeEnabled
                      ? t.LEARNING_JOURNEYS.DETAILS.STATEMENT_OF_ACCOMPLISHMENT(
                        credlyLink, selectCase,
                      )
                      : t.LEARNING_JOURNEYS.DETAILS.END_OF_JOURNEY()}
                  </span>
                </div>
              )
            ) : (
              <div css={limitedWidthContainerStyles}>
                <div className='text-center'>
                  <NewCollectionButton
                    className='new-collection'
                    onClick={handleNewCollectionButtonClick}
                  />
                </div>
                <div className='button-bar'>
                  <Button
                    variant='secondary'
                    onClick={handleFormCancel}
                  >
                    {t.FORM.CANCEL()}
                  </Button>
                  <Button
                    variant='primary'
                    disabled={!isValid || !isDirty}
                    onClick={methods.handleSubmit(handleFormSubmit)}
                    pendo-tag-name={config.pendo.learningJourneys.saveJourney}
                  >
                    {t.FORM.SAVE()}
                  </Button>
                </div>
              </div>
            )}
          </FormProvider>
        )}
      </div>
    </CourseUsageRegistryProvider>
  );
};

export const journeyCollectionContext = React.createContext<{
  getIsCourseLocked:(index: number) => boolean,
}>(null);

type CollectionProps = {
  index: number;
  swapCollection: (indexA: number, indexB: number) => void;
  removeFromCollections: (index?: number | number[]) => void;
  collection: FieldArrayWithId<FormData, 'collections', 'key'>;
  collections: FieldArrayWithId<FormData, 'collections', 'key'>[];
  collectionCompletionStats: CollectionCompletionStats;
};

const Collection = (props: CollectionProps) => {
  const {
    index,
    collection,
    collections,
    swapCollection,
    removeFromCollections,
    collectionCompletionStats,
  } = props;

  const canMoveUp = index > 0;
  const isFirstCollection = index === 0;
  const { formState, watch } = useFormContext();
  const { free } = useCourseUsageRegistryContext();
  const canMoveDown = index < collections.length - 1;
  const collectionName = watch(`collections.${index}.name`);
  const { isViewMode } = React.useContext(journeyDetailsContext);
  const angularServices = React.useContext(AngularServicesContext);
  const currentUserEnrollments = useSelector(getCurrentUserEnrollments);
  const collectionCoursesIds = collection.courses.map(course => course.id);
  const completionCriteria = watch(`collections.${index}.completionCriteria`);

  const {
    collectionCompletions,
    setCollectionCompleted,
  } = React.useContext(collectionCompletionsStatusContext);

  const collectionEnrollments = collectionCoursesIds.map(
    courseId => currentUserEnrollments.find(
      enrollment => courseId === enrollment.courseId,
    ),
  );

  const isPrevCollectionCompleted = isFirstCollection ? true : collectionCompletions[index - 1];

  const isCollectionCompleted = isPrevCollectionCompleted && collectionCompletionStats && (collectionCompletionStats.completed >= collectionCompletionStats.required);

  React.useLayoutEffect(() => {
    setCollectionCompleted(index, isCollectionCompleted);
  }, [index, isCollectionCompleted, setCollectionCompleted]);

  // If details is in view mode (journey home page), we will show real lock
  // state, if details is in edit mode we will always only unlock the first
  // collection.
  const isLocked = isViewMode ? !isPrevCollectionCompleted : !isFirstCollection;

  const handleDelete = () => {
    collections[index].courses.forEach((course) => {
      free(course.catalogId);
    });

    removeFromCollections(index);
  };

  const handleMoveUp = () => swapCollection(index, index - 1);
  const handleMoveDown = () => swapCollection(index, index + 1);

  const journeyCollectionContextValue = {
    getIsCourseLocked: (courseIndex: number) => {
      const isFirstCourse = courseIndex === 0;
      const prevCourseEnrollment = isFirstCourse ? null : collectionEnrollments[courseIndex - 1];
      const isPrevCourseCompleted = isFirstCourse ? true : prevCourseEnrollment?.completionStatus;
      const isCompletionInOrder = completionCriteria.value === CompletionCriteria.ALL_IN_ORDER;

      // Case for journey home page
      if (isViewMode) {
        // Case for when previous collection is completed
        if (isPrevCollectionCompleted) {
          // Case for when collection has completion in order criteria
          if (isCompletionInOrder) {
            return !isFirstCourse && !isPrevCourseCompleted;
          }

          // If collection is not completion in order
          return false;
        }

        // If previous collection is not completed, all any course should be
        // locked
        return true;
      }

      // Case for edit mode and is first collection
      if (isFirstCollection) {
        // If completion is in order, the only unlocked course should be the
        // first one
        if (isCompletionInOrder) {
          return !isFirstCourse;
        }

        // If completion is not in order, all the courses should be unlocked
        return false;
      }

      // Case for edit mode that is not in first collection. Any course whose
      // collection is locked should be locked, so in edit mode the only
      // unlocked collection is the first one.
      return true;
    },
  };

  return (
    <journeyCollectionContext.Provider value={journeyCollectionContextValue}>
      <div className='collection'>
        <div
          css={limitedWidthContainerStyles}
          className='limited-width-collection-container'
        >
          {(isViewMode ? !!collectionName : true) && (
            <FormCollectionHeader
              editable={!isViewMode}
              canMoveUp={canMoveUp}
              isLocked={isLocked}
              onMoveUp={handleMoveUp}
              onDelete={handleDelete}
              canMoveDown={canMoveDown}
              onMoveDown={handleMoveDown}
              className='collection-header'
              defaultValue={collection.name}
              name={`collections.${index}.name`}
            />
          )}
          <div className='text-center'>
            {isViewMode ? (
              <CompletionCriteriaLabel
                completionCriteria={completionCriteria}
                className='collection-completion-criteria'
              />
            ) : (
              <FormCompletionCriteriaDropdown
                defaultValue={completionCriteria}
                className='collection-completion-criteria'
                name={`collections.${index}.completionCriteria`}
                inputError={get(
                  formState.errors,
                  `collections.${index}.completionCriteria.meta`,
                )}
              />
            )}
          </div>
          {isViewMode ? (
            !!collection.description && (
              <ResponsivelyEmbeddedAngularHTML
                template={collection.description}
                className='collection-description'
                angularServices={angularServices}
              />
            )
          ) : (
            <NvFroala
              withForm
              immediateReactModelUpdate
              preset={FroalaViewMode.NORMAL}
              className='collection-description'
              name={`collections.${index}.description`}
              placeholder={t.LEARNING_JOURNEYS.DETAILS.COLLECTIONS.COLLECTION_DESCRIPTION_PLACEHOLDER()}
            />
          )}
          {!isViewMode && (
            <CollectionCourses
              className='collection-courses'
              autocompleteClassName='autocomplete'
              name={`collections.${index}.courses`}
              inputCourseSearchClassName='courses-search'
            />
          )}
        </div>
        {isViewMode && !!collection.courses.length && (
          <CoursesCarousel
            courses={collection.courses as CombinedCourse[]}
          />
        )}
      </div>
      {canMoveDown && (
        <CollectionSeparator className='separator' />
      )}
    </journeyCollectionContext.Provider>
  );
};

type CollectionCoursesProps = {
  name: string,
  className?: string,
  autocompleteClassName?: string,
  inputCourseSearchClassName?: string,
};

const CollectionCourses = (props: CollectionCoursesProps) => {
  const {
    name,
    className,
    autocompleteClassName,
    inputCourseSearchClassName,
  } = props;

  const { getIsCourseLocked } = React.useContext(journeyCollectionContext);

  const {
    free,
    allocate,
    registry,
  } = useCourseUsageRegistryContext();

  const {
    fields: courses,
    swap: swapCourse,
    append: appendToFormCourses,
    remove: removeFromCourses,
  } = useFieldArray<FormData, 'collections.0.courses', 'key'>({
    name: name as 'collections.0.courses',
    keyName: 'key',
  });

  const appendToCourses = (newCourse) => {
    appendToFormCourses(newCourse);
    allocate(newCourse.catalogId);
  };

  return (
    <React.Fragment>
      <div className={className}>
        {courses.map((course, index, array) => {
          const canMoveUp = index > 0;
          const canMoveDown = index < array.length - 1;
          const handleMoveUp = () => swapCourse(index, index - 1);
          const handleMoveDown = () => swapCourse(index, index + 1);

          const handleDelete = () => {
            removeFromCourses(index);
            free(course.catalogId);
          };

          return (
            <CourseRow
              key={course.id}
              canMoveUp={canMoveUp}
              onMoveUp={handleMoveUp}
              onDelete={handleDelete}
              canMoveDown={canMoveDown}
              onMoveDown={handleMoveDown}
              locked={getIsCourseLocked(index)}
              course={course as unknown as Course}
            />
          );
        })}
      </div>
      <InputCourseSearch
        onCourseAdd={appendToCourses}
        deniedCoursesDictionary={registry}
        className={inputCourseSearchClassName}
        autocompleteClassName={autocompleteClassName}
      />
    </React.Fragment>
  );
};

type NewCollectionButtonProps = {
  onClick: () => void,
  className?: string,
};

const NewCollectionButton = (props: NewCollectionButtonProps) => {
  const {
    onClick,
    className,
  } = props;

  const styles = css`
    font-size: 14px;
    line-height: 20px;
    align-items: center;
    display: inline-flex;
    font-family: ${openSans};

    .icon {
      margin-right: ${halfSpacing}px;
    }
  `;

  return (
    <ClickableContainer
      {...props}
      css={styles}
      onClick={onClick}
      pendo-tag-name={config.pendo.learningJourneys.addNewCollection}
      className={`btn btn-link hovered${className ? ` ${className}` : ''}`}
    >
      <NvIcon
        icon='add'
        size='smallest'
      />
      {t.LEARNING_JOURNEYS.DETAILS.COLLECTIONS.NEW_COLLECTION()}
    </ClickableContainer>
  );
};

type CollectionCompletions = { [index: number]: boolean };
const collectionCompletionsStatusContext = React.createContext<{
  collectionCompletions: CollectionCompletions,
  setCollectionCompleted:(index: number, completed: boolean) => void,
}>(null);

type CollectionCompletionStatusProviderProps = {
  children: React.ReactNode,
};

const CollectionCompletionsStatusProvider = (props: CollectionCompletionStatusProviderProps) => {
  const { children } = props;
  const [
    collectionCompletions,
    setCollectionCompletions,
  ] = React.useState<CollectionCompletions>({});

  const setCollectionCompleted = React.useCallback((index: number, completed: boolean) => {
    setCollectionCompletions((prev) => ({
      ...prev,
      [index]: completed,
    }));
  }, []);

  return (
    <collectionCompletionsStatusContext.Provider
      value={{
        collectionCompletions,
        setCollectionCompleted,
      }}
    >
      {children}
    </collectionCompletionsStatusContext.Provider>
  );
};

type Props = {
  className?: string,
  catalogId?: string,
  isViewMode?: boolean,
  onJourneyLoad?: (course?: any) => void,
  headerRef?: React.Ref<BrandingHeaderRef>,
};

const Details = (props: Props) => {
  const {
    className,
    headerRef,
    onJourneyLoad,
    isViewMode = false,
    catalogId: propsCatalogId,
  } = props;
  const containerRef = React.useRef();
  const onJourneyLoadRef = React.useRef<Function>();
  onJourneyLoadRef.current = onJourneyLoad;
  const { getJourney: fetchJourney } = useJourneyFormUtils();
  const paramsCatalogId = useParams<{ catalogId: string }>().catalogId;
  const [isSelfLoading, setIsSelfLoading] = React.useState(!isViewMode);
  const catalogId = propsCatalogId || paramsCatalogId;
  const currentJourney = useSelector((state) => getJourney(state, catalogId));
  const { isLoading: learningJourneyContextIsLoading } = React.useContext(LearningJourneyContext) || { isLoading: undefined };

  const isLoading = learningJourneyContextIsLoading === undefined
    ? isSelfLoading
    : learningJourneyContextIsLoading;

  React.useEffect(() => {
    if (!isViewMode) {
      setIsSelfLoading(true);
      fetchJourney(catalogId)
        .then((action) => {
          setIsSelfLoading(false);
          onJourneyLoadRef.current?.(action.payload.journey);
        })
        .catch(() => {})
        .finally(() => {
          onJourneyLoadRef.current?.();
        });
    }
  }, [catalogId, isViewMode, fetchJourney]);

  const journeyDetailsContextValue = {
    catalogId,
    isViewMode,
  };

  return (
    <div className={className} ref={containerRef}>
      <journeyDetailsContext.Provider value={journeyDetailsContextValue}>
        <PopoversContainerContext.Provider value={containerRef.current}>
          <OfferingsBrandingHeader
            ref={headerRef}
            value={{
              name: currentJourney?.name ?? '',
              brandColor: currentJourney?.headerColor,
              fontColor: currentJourney?.nameFontColor,
              logo: currentJourney?.logo ? {
                url: currentJourney.logo.url,
                size: currentJourney.logoSize,
              } : null,
              background: currentJourney?.headerBackground ? {
                url: currentJourney.headerBackground,
              } : null,
            }}
          />
          {isLoading ? (
            <div
              css={css`
                ${masterContainerStyles};
                ${limitedWidthContainerStyles};
              `}
            >
              <LoadingPlaceholder />
            </div>
          ) : <JourneyContent />}
        </PopoversContainerContext.Provider>
      </journeyDetailsContext.Provider>
    </div>
  );
};

export default Details;
